/**
 * Custom StackBarChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { StackBarChartComponent as ParentStackBarChartComponent } from '@shared-lib/chart-components/stack-bar-chart/stack-bar-chart.component';
import { buildConfigurableTooltipFormatter, buildConfigurableAxisLabel } from '@shared-lib/utils/chart.utils';
import { getSeriesColor, ColorConfig } from '@shared-lib/utils/color.utils';
import { buildSecondaryXAxisOption, buildSecondaryYAxisOption, getDoubleAxisGridAdjustments } from '@shared-lib/utils/axis.utils';

@Component({
  selector: 'app-stack-bar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stack-bar-chart.component.html',
  styleUrls: ['./stack-bar-chart.component.scss']
})
export class StackBarChartComponent extends ParentStackBarChartComponent {
  private readonly gapCategoryPrefix = '__gap__';

  private buildCategoriesWithGroupGaps(
    categories: string[],
    seriesData: any[],
    categoryToGroup: Record<string, string | undefined>
  ): { categories: string[]; seriesData: any[] } {
    if (!categories?.length || !seriesData?.length) {
      return { categories, seriesData };
    }

    const categoriesWithGaps: string[] = [];
    const indexMap: number[] = [];
    let gapIndex = 0;

    for (let i = 0; i < categories.length; i++) {
      categoriesWithGaps.push(categories[i]);
      indexMap.push(i);

      const currentGroup = categoryToGroup[categories[i]];
      const nextGroup = i + 1 < categories.length ? categoryToGroup[categories[i + 1]] : undefined;
      if (currentGroup && nextGroup && currentGroup !== nextGroup) {
        categoriesWithGaps.push(`${this.gapCategoryPrefix}${gapIndex++}`);
        indexMap.push(-1);
      }
    }

    if (!gapIndex) {
      return { categories, seriesData };
    }

    const seriesWithGaps = seriesData.map((series: any) => ({
      ...series,
      data: indexMap.map((idx) => (idx >= 0 ? series.data?.[idx] ?? 0 : '-')),
    }));

    return { categories: categoriesWithGaps, seriesData: seriesWithGaps };
  }

  // Constructor is inherited from parent to avoid dependency injection issues

  protected override loadAndRenderChart(filters?: any): any {
    // TODO: Add your custom logic here
    return super.loadAndRenderChart(filters);
  }

  protected override initChart(chartData: any): void {
    if (!chartData) return;

    // If a specific height is provided in widget config, apply it with `!important`
    // to override global stylesheet rules that use `!important` variables.
    try {
      const id = this.data?.widgetId;
      const h = this.data?.height;
      if (id && h) {
        const el = document.getElementById(id);
        if (el && el.style) {
          el.style.setProperty('height', String(h), 'important');
        }
      }
    } catch (e) {
      // ignore
    }

    const config = this.data?.chartConfig || {};
    let categories: string[] = [];
    let seriesData: any[] = [];

    const dataKey = this.data?.dataKey;
    const seriesKey = this.data?.seriesKey;
    const categoryFields: string[] = this.data?.categoryFields || ['quarter', 'year'];
    const categoryDelimiter: string = this.data?.categoryDelimiter ?? ' ';
    const groupByField: string = this.data?.groupByField || this.data?.oemKey || categoryFields[0] || 'oemName';
    const categoryToGroup: Record<string, string | undefined> = {};
    const buildCategoryString = (item: any): string =>
      categoryFields.map((field) => item[field] ?? '').join(categoryDelimiter);

    if (dataKey && chartData[dataKey] && Array.isArray(chartData[dataKey])) {
      const flatData = chartData[dataKey];
      // Add combined category key so sourceDataMap can find items by x-axis label.
      flatData.forEach((d: any) => {
        d._category = buildCategoryString(d);
        categoryToGroup[d._category] = d?.[groupByField];
      });
      this.wrapper.setSourceData(flatData, '_category', this.data?.seriesKey || 'series');
      categories = Array.from(new Set(flatData.map((d: any) => d._category)));
      const uniqueSeriesNames = Array.from(new Set(flatData.map((d: any) => d[seriesKey] || 'Unknown')));
      seriesData = uniqueSeriesNames.map((name: any) => {
        const seriesPoints = categories.map((cat) => {
          const point = flatData.find((d: any) => d[seriesKey] === name && d._category === cat);
          return point ? { ...point, value: point.value } : 0;
        });
        return { name, type: 'bar', stack: 'total', emphasis: { focus: 'series' }, data: seriesPoints };
      });
    } else if (!chartData.category && !chartData.series) {
      const seriesNames = Object.keys(chartData);
      if (seriesNames.length > 0) {
        const firstSeriesData = chartData[seriesNames[0]];
        if (Array.isArray(firstSeriesData)) {
          categories = firstSeriesData.map((item: any) => buildCategoryString(item));
          firstSeriesData.forEach((item: any) => {
            const key = buildCategoryString(item);
            categoryToGroup[key] = item?.[groupByField];
          });
        }
        seriesData = seriesNames.map((name) => ({
          name, type: 'bar', stack: 'total', emphasis: { focus: 'series' },
          data: Array.isArray(chartData[name]) ? chartData[name].map((d: any) => d.value) : [],
        }));
      }
    } else if (chartData.category && chartData.series) {
      categories = chartData.category;
      categories.forEach((cat: string) => {
        const raw = String(cat || '');
        const parts = raw.split(categoryDelimiter);
        if (parts.length > 1) {
          categoryToGroup[cat] = parts.slice(0, -1).join(categoryDelimiter);
        }
      });
      seriesData = chartData.series.map((s: any) => ({
        name: s.name, type: 'bar', stack: 'total', emphasis: { focus: 'series' }, data: s.data,
      }));
    }

    const spacedData = this.buildCategoriesWithGroupGaps(categories, seriesData, categoryToGroup);
    categories = spacedData.categories;
    seriesData = spacedData.seriesData;

    this.allSeriesNames = seriesData.map((s) => s.name);
    this.allCategories = categories;

    // Color consistency — use wrapper
    const colorConfig: ColorConfig = {
      colorMap: config.colorMap || {},
      topNColorsByStackPosition: config.topNColorsByStackPosition || [],
    };
    const coloredSeries = seriesData.map((s, index) => {
      let seriesColor = getSeriesColor(index, s.name, colorConfig);
      seriesColor = this.wrapper.applyColorConsistency(s.name, seriesColor) || seriesColor;
      // Only apply border to the bottom series to avoid internal borders
      const borderStyle = index === 0 ? { borderColor: '#000000', borderWidth: 0.5 } : {};
      return { ...s, ...(seriesColor ? { itemStyle: { color: seriesColor, ...borderStyle } } : {}) };
    });

    const tooltipCfg = this.data?.chartConfig?.tooltip;
    const formatter = buildConfigurableTooltipFormatter(tooltipCfg);
    const gridAdj = getDoubleAxisGridAdjustments(this.data);

    const primaryXAxis: any = {
      type: 'category',
      data: categories,
      triggerEvent: true,
      splitLine: { show: false },
      ...(this.data?.enableDoubleXAxis
        ? {
          axisLabel: {
            interval: 0,
            formatter: (val: string) => {
              if (String(val).startsWith(this.gapCategoryPrefix)) return '';
              const yearMatch = String(val).match(/(19|20)(\d{2})\s*$/);
              if (yearMatch) return yearMatch[2];
              const parts = String(val).split(categoryDelimiter);
              return parts[parts.length - 1];
            },
          },
        }
        : {}),
    };
    const secondaryX = buildSecondaryXAxisOption(this.data, categories);
    if (secondaryX) {
      secondaryX.axis.splitLine = { show: false };
    }
    const xAxisOption = secondaryX ? [primaryXAxis, secondaryX.axis] : primaryXAxis;

    const primaryYAxis: any = {
      type: 'value',
      splitLine: { show: false },
      ...(buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis)
        ? { axisLabel: buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis) }
        : {}),
    };
    const secondaryY = buildSecondaryYAxisOption(this.data);
    if (secondaryY) {
      secondaryY.splitLine = { show: false };
    }
    const yAxisOption = secondaryY ? [primaryYAxis, secondaryY] : primaryYAxis;

    let option: any = {
      tooltip: {
        trigger: tooltipCfg?.trigger || 'axis',
        appendToBody: true,
        axisPointer: { type: 'shadow' },
        ...(formatter ? { formatter } : {}),
      },
      ...(this.data?.chartConfig?.legend ? {
        legend: {
          data: seriesData.map((s) => s.name),
          top: 10,
          right: 10,
          type: 'scroll',
        }
      } : {}),
      grid: {
        top: this.data?.chartConfig?.legend ? '20%' : '15%',
        left: '2%',
        right: `${4 + gridAdj.extraRight}%`,
        bottom: `${20 + gridAdj.extraBottom}%`,
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: coloredSeries,
    };

    if (!this.myChart) return;

    // Custom modifications start here
    if (option.yAxis) {
      if (Array.isArray(option.yAxis)) {
        option.yAxis = option.yAxis.map((axis: any) => ({
          ...axis,
          axisLabel: { ...(axis?.axisLabel || {}), show: false },
        }));
      } else {
        option.yAxis = {
          ...option.yAxis,
          axisLabel: { ...(option.yAxis?.axisLabel || {}), show: false },
        };
      }
    }

    // Add totals labels above stacked bars
    try {
      // Set gaps between bars (configurable via chartConfig)
      option.barCategoryGap = config.barCategoryGap ?? '10%';
      option.barGap = config.barGap ?? '20%';
      const configuredMinInsideLabelPercent = Number(
        this.data?.chartConfig?.insideLabelMinPercent ?? this.data?.insideLabelMinPercent ?? 7
      );
      const minInsideLabelPercent = Number.isFinite(configuredMinInsideLabelPercent)
        ? Math.max(0, configuredMinInsideLabelPercent)
        : 7;
      const configuredMinInsideBarRatio = Number(
        this.data?.chartConfig?.insideLabelMinBarRatio ?? this.data?.insideLabelMinBarRatio ?? 0
      );
      const minInsideBarRatio = Number.isFinite(configuredMinInsideBarRatio)
        ? Math.max(0, configuredMinInsideBarRatio)
        : 0;
      const seriesList: any[] = option.series || [];
      const pointsCount = seriesList[0]?.data?.length || 0;
      const totals: number[] = [];
      for (let i = 0; i < pointsCount; i++) {
        let sum = 0;
        for (const s of seriesList) {
          const v = s?.data?.[i];
          if (v == null) continue;
          if (typeof v === 'object') sum += Number(v.value ?? v.val ?? 0) || 0;
          else sum += Number(v) || 0;
        }
        totals.push(sum);
      }
      const maxTotal = totals.length ? Math.max(...totals, 0) : 0;

      // Add percentage labels inside each bar (per data point) based on totals
      try {
        seriesList.forEach((s: any, seriesIdx: number) => {
          const newData: any[] = (s.data || []).map((d: any, idx: number) => {
            const raw = (typeof d === 'object') ? (Number(d.value ?? d.val ?? 0) || 0) : (Number(d) || 0);
            const total = totals[idx] || 0;
            const pct = total > 0 ? Math.round((raw / total) * 100) : 0;
            const barRatio = maxTotal > 0 ? (raw / maxTotal) : 0;
            // keep existing object properties if present
            const base = (typeof d === 'object') ? { ...d, value: raw } : { value: raw };
            // add label per point
            base.label = {
              show: raw > 0
                && total > 0
                && pct >= minInsideLabelPercent
                && barRatio >= minInsideBarRatio,
              position: 'inside',
              formatter: pct + '%',
              color: '#ffffff',
              fontSize: 9,
            };
            return base;
          });
          s.data = newData;
          // ensure series label at series level is disabled to avoid duplicates
          s.label = s.label || {};
          s.label.show = false;
        });
      } catch (e) {
        console.warn('[CustomStackBarChartComponent] failed to add inside percentage labels', e);
      }

      const totalsSeries = {
        name: 'Total',
        type: 'bar',
        data: totals,
        itemStyle: { color: 'transparent' },
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) => {
            const v = Number(params?.value) || 0;
            if (v <= 0) return '';
            return Math.round(v / 1_000_000) + 'M';
          },
          fontSize: 8, // Small size to fit bar width
          fontWeight: '600',
          align: 'center',
        },
        tooltip: { show: false },
        emphasis: { disabled: true },
        silent: true,
        barGap: '-100%',
        // barCategoryGap: '0%', // Remove to use global
        barWidth: seriesList[0]?.barWidth ?? undefined,
        z: 1000,
      };

      option.series = [...seriesList, totalsSeries];

      // Add dataZoom slider if more than 5 categories
      if (option.xAxis && option.xAxis.data && option.xAxis.data.length > 5) {
        option.dataZoom = [
          {
            type: 'slider',
            xAxisIndex: 0,
            start: 0,
            end: Math.min(100, (5 / option.xAxis.data.length) * 100),
            height: 20,
            bottom: 10,
          }
        ];
        // Adjust grid bottom to make space for slider
        if (option.grid) {
          option.grid.bottom = `${30 + (option.grid.bottom ? parseInt(option.grid.bottom) : 20)}%`;
        }
      }
    } catch (e) {
      console.warn('[CustomStackBarChartComponent] failed to add totals labels', e);
    }

    if (this.myChart) {
      this.myChart.setOption(option, true);
      this.myChart.hideLoading();

      this.wrapper.afterRender();
    }
  }

  protected override addResizeListener(): any {
    // TODO: Add your custom logic here
    return super.addResizeListener();
  }
}
